import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Clock, Flame, Heart, Sparkles, Star, Users, Film, Tv,
} from 'lucide-react';
import Hero from '../components/home/Hero';
import Rail from '../ui/Rail';
import Poster from '../ui/Poster';
import PersonCard from '../ui/PersonCard';
import usePersonPhotos from '../hooks/usePersonPhotos';
import RecommendedMovies from '../components/RecommendedMovies';
import UpcomingEvents from '../components/UpcomingEvents';
import SeriesCalendarWidget from '../components/SeriesCalendarWidget';
import { useCatalog } from '../providers/CatalogProvider';
import { byGenre, genreRanking, newArrivals, similarTo, topRated } from '../lib/catalog';
import { actorPath, explorePath } from '../lib/routes';

/* Référence stable : `catalog?.titles || []` fabriquerait un tableau neuf à
   chaque rendu et invaliderait tous les `useMemo` de la page. */
const NONE = [];

const HERO_SIZE = 6;
const FACES = 16;

/** Lien « tout voir » homogène, à droite de chaque rail. */
function SeeAll({ to }) {
  const navigate = useNavigate();
  return (
    <button type="button" className="section-link" onClick={() => navigate(to)}>
      Tout voir
      <ArrowRight aria-hidden="true" />
    </button>
  );
}

/**
 * Accueil éditorial — hero + rails.
 *
 * Toutes les sélections sont dérivées du catalogue déjà en mémoire : aucune
 * requête supplémentaire n'est émise pour construire cette page.
 */
export default function HomePage({ onOpenTheater }) {
  const navigate = useNavigate();
  const catalog = useCatalog();

  const titles = catalog?.titles ?? NONE;
  const open = catalog?.onOpenTitle;
  const toggleList = catalog?.onToggleWatchlist;

  /* ── Sélections ───────────────────────────────────────────────── */

  const heroTitles = useMemo(() => {
    const featured = titles.filter((t) => t.featured && (t.backdrop || t.poster));
    const pool = featured.length >= 3 ? featured : topRated(titles, 40);
    return pool.filter((t) => t.backdrop || t.poster).slice(0, HERO_SIZE);
  }, [titles]);

  /** Reprendre : trié par dernière lecture, pas par avancement. */
  const resume = useMemo(() => {
    const byId = new Map(titles.map((t) => [String(t.id), t]));
    const episodes = new Map((catalog?.movies || []).map((m) => [String(m.id), m]));

    return (catalog?.progressList || [])
      .filter((entry) => entry.percentage > 2 && entry.percentage < 92)
      .map((entry) => byId.get(String(entry.movie_id)) || episodes.get(String(entry.movie_id)))
      .filter(Boolean)
      .slice(0, 14);
  }, [catalog?.progressList, catalog?.movies, titles]);

  const myList = useMemo(
    () => titles.filter((t) => catalog?.isInList(t)).slice(0, 18),
    [titles, catalog]
  );

  const top10 = useMemo(() => topRated(titles, 10), [titles]);
  const fresh = useMemo(() => newArrivals(titles, 18), [titles]);

  const films = useMemo(
    () => titles.filter((t) => t.type !== 'serie').sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 18),
    [titles]
  );
  const series = useMemo(
    () => titles.filter((t) => t.type === 'serie').sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 18),
    [titles]
  );

  /** Rebond sur le dernier titre entamé. */
  const because = useMemo(() => {
    const reference = resume[0] || myList[0];
    if (!reference) return null;
    const suggestions = similarTo(titles, reference, 18);
    return suggestions.length >= 6 ? { reference, suggestions } : null;
  }, [resume, myList, titles]);

  const genres = useMemo(() => genreRanking(titles).slice(0, 6), [titles]);

  /** Têtes d'affiche : les plus présentes dans la bibliothèque. */
  const faces = useMemo(
    () => (catalog?.people.actors || []).slice(0, FACES),
    [catalog?.people.actors]
  );
  const { people: facePhotos } = usePersonPhotos(faces.map((person) => person.name));

  /* ── Rendu ────────────────────────────────────────────────────── */

  const posterFor = (title, index, options = {}) => (
    <Poster
      key={title.id}
      index={index}
      title={title}
      {...options}
      onOpen={open}
      onToggleList={toggleList}
      inList={catalog?.isInList(title)}
      watched={catalog?.isWatched(title)}
      progress={catalog?.progressOf(title)}
    />
  );

  if (!titles.length) {
    return (
      <main className="page-bottom-space" style={{ paddingTop: 'calc(var(--h-header) + 2rem)' }}>
        <div className="shell">
          <div className="skeleton" style={{ height: 'min(70svh, 560px)', borderRadius: 'var(--r-2xl)' }} />
          <div className="grid-posters mt-8">
            {Array.from({ length: 12 }).map((_, index) => (
              <div key={index} className="skeleton" style={{ aspectRatio: '2 / 3' }} />
            ))}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="page-bottom-space">
      <Hero
        titles={heroTitles}
        onOpen={open}
        onToggleList={toggleList}
        isInList={catalog?.isInList}
      />

      <div className="stack" style={{ gap: 'clamp(2rem, 4.5vw, 3.5rem)', marginTop: 'clamp(1.5rem, 4vw, 3rem)' }}>
        {/* Séances programmées : information périssable, donc placée haut. */}
        <div className="shell">
          <UpcomingEvents onOpenTheater={onOpenTheater} />
        </div>

        {resume.length > 0 && (
          <Rail
            title="Reprendre"
            icon={<Clock aria-hidden="true" />}
            subtitle="Là où vous vous êtes arrêté"
            itemClass="rail-item-wide"
          >
            {resume.map((title, index) => (
              <Poster
                key={`resume-${title.id}`}
                index={index}
                title={title}
                wide
                onOpen={open}
                progress={catalog?.progressOf(title)}
                onDismiss={catalog?.onForgetProgress}
                dismissLabel="Retirer de la reprise"
              />
            ))}
          </Rail>
        )}

        {top10.length >= 5 && (
          <Rail
            title="Le top 10"
            icon={<Flame aria-hidden="true" />}
            subtitle="Les mieux notés de la bibliothèque"
            itemClass="rail-item-lg"
            action={<SeeAll to={explorePath({ sort: 'rating' })} />}
          >
            {top10.map((title, index) => (
              <div className="rank" key={`top-${title.id}`}>
                <span className="rank-num" data-n={index + 1} aria-hidden="true">
                  {index + 1}
                </span>
                {/* Sans légende : le chiffre géant sert de repère et le
                    texte n'empiète pas sur le numéro suivant. */}
                {posterFor(title, index, { showMeta: false })}
              </div>
            ))}
          </Rail>
        )}

        <RecommendedMovies user={catalog?.currentUser} onSelectMovie={open} />

        {fresh.length > 0 && (
          <Rail
            title="Nouveautés"
            icon={<Sparkles aria-hidden="true" />}
            subtitle="Fraîchement ajoutés"
            action={<SeeAll to={explorePath({ sort: 'recent' })} />}
          >
            {fresh.map(posterFor)}
          </Rail>
        )}

        {myList.length > 0 && (
          <Rail
            title="Ma liste"
            icon={<Heart aria-hidden="true" />}
            action={<SeeAll to={explorePath({ view: 'list' })} />}
          >
            {myList.map(posterFor)}
          </Rail>
        )}

        {/* ── Têtes d'affiche : l'entrée du parcours par acteur ── */}
        {faces.length > 0 && (
          <Rail
            title="Têtes d'affiche"
            icon={<Users aria-hidden="true" />}
            subtitle="Les visages les plus présents dans la bibliothèque"
            itemClass="rail-item-face"
            action={<SeeAll to="/acteurs" />}
          >
            {faces.map((person, index) => (
              <PersonCard
                key={person.key}
                index={index}
                name={person.name}
                photo={facePhotos[person.name]?.photo}
                note={`${person.count} titre${person.count > 1 ? 's' : ''}`}
                onOpen={() => navigate(actorPath(person.name))}
              />
            ))}
          </Rail>
        )}

        {because && (
          <Rail
            title={`Parce que vous avez regardé ${because.reference.title}`}
            icon={<Star aria-hidden="true" />}
          >
            {because.suggestions.map(posterFor)}
          </Rail>
        )}

        {films.length > 0 && (
          <Rail
            title="Films"
            icon={<Film aria-hidden="true" />}
            action={<SeeAll to={explorePath({ type: 'film' })} />}
          >
            {films.map(posterFor)}
          </Rail>
        )}

        {series.length > 0 && (
          <Rail
            title="Séries"
            icon={<Tv aria-hidden="true" />}
            action={<SeeAll to={explorePath({ type: 'serie' })} />}
          >
            {series.map(posterFor)}
          </Rail>
        )}

        <div className="shell">
          <SeriesCalendarWidget />
        </div>

        {genres.map((genre) => {
          const selection = byGenre(titles, genre.name, 18);
          if (selection.length < 6) return null;
          return (
            <Rail
              key={genre.name}
              title={genre.name}
              subtitle={`${genre.count} titres`}
              action={<SeeAll to={explorePath({ genres: genre.name })} />}
            >
              {selection.map(posterFor)}
            </Rail>
          );
        })}
      </div>
    </main>
  );
}
